"use client";

import { useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { X } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";

export interface EquipmentItem {
  id: string;
  name: string;
  tags: string[];
  category: string;
  image: string;
  description?: string;
}

interface EquipmentDialogProps {
  item: EquipmentItem | null;
  onClose: () => void;
  showCta?: boolean;
}

export default function EquipmentDialog({ item, onClose, showCta = true }: EquipmentDialogProps) {
  const t = useTranslations("EquipmentDialog");
  const locale = useLocale() as "hr" | "en";
  const contactHref = locale === "en" ? "/en/#kontakt" : "/#kontakt";

  // Close on Escape key
  useEffect(() => {
    if (!item) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [item, onClose]);

  if (!item) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl overflow-y-auto rounded-3xl bg-white shadow-xl"
        style={{ maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#18181b] shadow-sm transition-colors hover:bg-[#f4f4f5]"
          aria-label={t("closeAria")}
        >
          <X size={16} />
        </button>

        {/* Body */}
        <div className="flex flex-col sm:flex-row">
          {/* Left — image */}
          <div className="relative flex h-64 w-full flex-shrink-0 items-center justify-center rounded-t-3xl border-b border-[#E4E4E7] bg-white sm:h-auto sm:w-[45%] sm:rounded-l-3xl sm:rounded-tr-none sm:border-b-0 sm:border-r">
            <div className="relative h-52 w-52 sm:h-64 sm:w-64">
              <Image
                src={item.image}
                alt={item.name}
                fill
                className="object-contain"
              />
            </div>
          </div>

          {/* Right — content */}
          <div className="flex flex-1 flex-col justify-between p-6 sm:p-8">
            <div>
              <h2 className="mb-3 text-2xl font-bold tracking-tight text-[#18181b] sm:text-3xl">
                {item.name}
              </h2>

              <p className="mb-5 text-sm leading-relaxed text-[#52525b]">
                {item.description ?? t("defaultDescription")}
              </p>

              {/* Tags */}
              <div className="flex flex-wrap gap-2">
                {item.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-[#E4E4E7] bg-white px-3 py-1 text-xs font-medium text-[#18181b]"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* CTA */}
            {showCta && (
              <Link
                href={contactHref}
                onClick={onClose}
                className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-[#18181b] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[#3f3f46]"
              >
                {t("cta")}
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
